import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import _ from 'lodash';
import { default as React, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import { connect } from 'react-redux';
import Select, { components } from 'react-select';
import * as yup from 'yup';
import { Actions as AUTHENTICATION_ACTIONS } from '../../store/actions/authenticationActions';
import { AddButton } from '../../styledComponents/GlobalStyle';
import { itemToOptions } from '../utils/GlobalFunctions';
import { whiteLabelSelect } from '../utils/ReactSelectStyles';
import ValidationMessage from '../utils/ValidationMessage';

const CustomMultiValue = (props) => {
  const VALUE_LIMIT = 3;
  const { index, getValue } = props;
  const hiddenLength = getValue().length - VALUE_LIMIT;

  return index < VALUE_LIMIT ? (
    <components.MultiValue {...props} />
  ) : index === VALUE_LIMIT ? (
    <div className='option-label'>{`+ ${hiddenLength} selected`}</div>
  ) : null;
};

const CustomOption = (props) => {
  let disabled = false;
  props.options.map((group) => {
    if (group.label === 'Unavailable Organisations') {
      for (const option of group.options) {
        if (option.label === props.label) {
          if (option.isDisabled) {
            disabled = true;
          }
        }
      }
    }
  });
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: disabled === true ? '#D9D9D9' : 'white',
        color: disabled === true ? '#5E5E5E' : 'black',
      }}>
      <components.Option {...props}>
        <input type='checkbox' checked={props.isSelected} disabled={disabled} onChange={() => null} />{' '}
        <label>{props.label}</label>
      </components.Option>
    </div>
  );
};

const validateUserSchema = yup.object().shape({
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  email: yup.string().email().required('Email is required'),
  selectedPermissions: yup.array().min(1, 'Please select at least one permission').required('Permissions are required'),
});

const InviteUserForm = ({
  inviteNewUser,
  cyfAdmin,
  setShowInviteUserForm,
  showInviteUserForm,
  whiteLabelAdminOrgs,
  consultantAdminOrgs,
  branding,
  permissions,
  orgManager,
}) => {
  const defaultStructure = {
    organisation: { view: true, edit: false, manage: false },
    organisationMetrics: { view: true, create: false, edit: false, delete: false },
    facilities: { '*': { view: true, edit: true, delete: true } },
    facilityMetrics: { view: true, create: true, edit: true, delete: true },
    reports: { view: true, create: true },
    usageData: { view: true, create: true, edit: true, delete: true },
    dashboard: { view: true, create: true, edit: true, delete: true },
    developer: { read: false, write: false },
  };
  const permissionsStructure = {
    organisation: { view: false, edit: false, manage: false },
    organisationMetrics: { view: false, create: false, edit: false, delete: false },
    facilities: { '*': { view: false, edit: false, delete: false } },
    facilityMetrics: { view: false, create: false, edit: false, delete: false },
    reports: { view: false, create: false },
    usageData: { view: false, create: false, edit: false, delete: false },
    dashboard: { view: false, create: false, edit: false, delete: false },
    developer: { read: false, write: false },
  };
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: 0,
    role: '',
    whiteLabelAdmin: null,
    consultantAdmin: null,
    permissions: defaultStructure,
  });
  const [showWLAdminField, setShowWLAdminField] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [validationError, setValidationError] = useState([]);
  const generalUserOption = permissions.find((opt) => opt.label === 'General User');
  const [selectedPermissions, setSelectedPermissions] = useState(generalUserOption ? [generalUserOption] : []);
  const { primaryColor, tooltipBackground, tooltipText } = branding || {};

  const updateField = (e) => {
    const editedUser = { ...user };
    editedUser[e.target.name] = e.target.value;
    setUser(editedUser);
  };

  const handleInviteUser = async () => {
    try {
      setProcessing(true);
      await validateUserSchema.validate({ ...user, selectedPermissions }, { abortEarly: false });
      await inviteNewUser(user);
      setShowInviteUserForm(!showInviteUserForm);
    } catch (error) {
      console.log('Error inviting user', error);
      if (error?.inner) {
        setValidationError(error.inner);
      }
    } finally {
      setProcessing(false);
    }
  };

  const updatePermissionsOnChange = (selectedOptions, permissionsObject, permissionsStructure) => {
    // Reset all permissions to false based on the full structure
    const resetPermissions = (structure, targetObject) => {
      Object.entries(structure).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          targetObject[key] = {};
          value.forEach((perm) => {
            targetObject[key][perm] = false; // Reset to false
          });
        } else if (typeof value === 'object') {
          targetObject[key] = {};
          resetPermissions(value, targetObject[key]); // Recursive reset
        } else if (typeof value === 'boolean') {
          targetObject[key] = false; // Reset boolean permissions
        }
      });
    };

    // Apply selected permissions
    const applyPermissions = (permissionsStructure, targetObject) => {
      Object.entries(permissionsStructure).forEach(([key, subPermissions]) => {
        if (Array.isArray(subPermissions)) {
          targetObject[key] = targetObject[key] || {};
          subPermissions.forEach((perm) => {
            targetObject[key][perm] = true; // Set to true based on selection
          });
        } else if (typeof subPermissions === 'object') {
          targetObject[key] = targetObject[key] || {};
          applyPermissions(subPermissions, targetObject[key]); // Recursive application
        } else if (subPermissions === true) {
          targetObject[key] = true; // Set boolean permissions
        }
      });
    };

    // Step 1: Reset all permissions
    resetPermissions(permissionsStructure, permissionsObject);

    // Step 2: Loop through selected options to apply true values
    selectedOptions.forEach((option) => {
      applyPermissions(option.value, permissionsObject);
    });

    // Step 3: Ensure facilities[*].view is true if Usage Data Access is selected but Facility Access is not
    const hasUsageDataAccess = selectedOptions.some((opt) => opt.label === 'Usage Data Access');
    const hasFacilityAccess = selectedOptions.some((opt) => opt.label === 'Facility Access');
    if (hasUsageDataAccess && !hasFacilityAccess) {
      if (permissionsObject && permissionsObject.facilities && permissionsObject.facilities['*']) {
        permissionsObject.facilities['*'].view = true;
      }
    }
  };

  const handlePermissionsChange = (selectedOptions) => {
    if (!selectedOptions) {
      setUser({
        ...user,
        permissions: permissionsStructure,
      });
      setSelectedPermissions([]);
      return;
    } else {
      setSelectedPermissions(selectedOptions);
      const permissionsObject = user?.permissions || {};
      updatePermissionsOnChange(selectedOptions, permissionsObject, permissionsStructure);
      setUser({ ...user, permissions: permissionsObject });
    }
  };

  return (
    <div className='modal' style={{ display: 'flex', alignItems: 'baseline', background: 'rgba(0,0,0,.5)' }}>
      <div className='modal-content' style={{ margin: '70px auto', width: '400px', height: 'auto' }}>
        <div className='modal-header' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 className='modal-title'>Invite New User</h4>
          <button id='close-button' onClick={() => setShowInviteUserForm(!showInviteUserForm)} className='modal-close'>
            <i className='fa fa-times' />
          </button>
        </div>
        <div className='modal-body' style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div>
            <input
              id='firstName'
              type='text'
              className='form-control'
              name='firstName'
              onChange={updateField}
              placeholder='First Name *'
              style={{ width: '350px', marginTop: 20, height: '5vh' }}
            />
            <ValidationMessage
              message={_.find(validationError, { path: 'firstName' })?.message}
              style={{ marginBottom: 0 }}
            />
          </div>

          <div>
            <input
              id='surname'
              type='text'
              className='form-control'
              name='lastName'
              onChange={updateField}
              placeholder='Surname *'
              style={{ width: '350px', marginTop: 15, height: '5vh' }}
            />
            <ValidationMessage
              message={_.find(validationError, { path: 'lastName' })?.message}
              style={{ marginBottom: 0 }}
            />
          </div>

          <div>
            <input
              id='role'
              type='text'
              className='form-control'
              name='role'
              onChange={updateField}
              placeholder='Job Title'
              style={{ width: '350px', marginTop: 15, height: '5vh' }}
            />
          </div>

          <div className='input-with-icon-block'>
            <input
              id='email'
              type='email'
              className='form-control'
              name='email'
              onChange={updateField}
              placeholder='Email (case sensitive) *'
              style={{ width: '350px', marginTop: 15, height: '5vh' }}
            />
            <Tooltip
              title={
                <span style={{ fontSize: 12 }}>
                  Email addresses are case sensitive throughout the application. Please enter your email exactly as you wish
                  to use it going forward. You will receive a confirmation of registration when this process is completed,
                  please retain this email for reference to your registered email ID.
                </span>
              }
              arrow
              placement='left'
              componentsProps={{
                tooltip: { sx: { backgroundColor: tooltipBackground, color: tooltipText } },
                arrow: { sx: { color: tooltipBackground } },
              }}>
              <i
                className='fa fa-info-circle'
                style={{
                  transform: 'none',
                  top: _.find(validationError, { path: 'email' })?.message ? '40%' : '50%',
                  color: primaryColor,
                }}
              />
            </Tooltip>
            <ValidationMessage message={_.find(validationError, { path: 'email' })?.message} style={{ marginBottom: 0 }} />
          </div>

          <div style={{ marginTop: 15, marginBottom: 15 }}>
            <PhoneInput
              country={'gb'}
              inputProps={{ name: 'phoneNumber' }}
              placeholder='Phone Number *'
              inputClass='inputClassInviteUser'
              onChange={(phoneNumber) => {
                const editedUser = { ...user };
                editedUser['phoneNumber'] = phoneNumber;
                setUser(editedUser);
              }}
            />
          </div>

          <div style={{ width: '90%' }}>
            <div>
              {orgManager && (
                <p
                  id='show-org-manager'
                  style={{ fontSize: 12, fontWeight: 'bold', display: 'inline-block', marginTop: 10, cursor: 'pointer' }}
                  onClick={() => setShowPermissions(!showPermissions)}>
                  {!showPermissions ? (
                    <span>
                      Set Specific Permissions <i style={{ marginLeft: 5 }} className='fa-solid fa-chevron-down' />
                    </span>
                  ) : (
                    <span>
                      Set Specific Permissions <i style={{ marginLeft: 5 }} className='fa-solid fa-chevron-up' />
                    </span>
                  )}
                </p>
              )}

              <div style={{ width: '350px', display: orgManager && showPermissions ? 'block' : 'none', marginBottom: 10 }}>
                <Select
                  id='permissions-dropdown'
                  options={permissions}
                  value={selectedPermissions}
                  styles={{
                    option: (base, state) => ({
                      ...base,
                      height: '100%',
                      color: state.isSelected && 'black',
                      backgroundColor: state.isSelected ? 'white' : state.isFocused && 'white',
                    }),
                  }}
                  isClearable={true}
                  isMulti={true}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  placeholder='User Permissions *'
                  onChange={handlePermissionsChange}
                  components={{
                    MultiValue: (props) => <CustomMultiValue {...props} />,
                    Option: CustomOption,
                  }}
                />
                <ValidationMessage
                  message={_.find(validationError, { path: 'selectedPermissions' })?.message}
                  style={{ marginBottom: 0 }}
                />
              </div>
            </div>

            {cyfAdmin && (
              <p
                id='show-white-label'
                style={{ fontSize: 12, fontWeight: 'bold', display: 'inline-block', marginTop: 10, cursor: 'pointer' }}
                onClick={() => setShowWLAdminField(!showWLAdminField)}>
                {!showWLAdminField ? (
                  <span>
                    <i className='fa-solid fa-triangle-exclamation' /> Consultant or White Label Admin? Click here
                  </span>
                ) : (
                  <span>Close</span>
                )}
              </p>
            )}
          </div>

          <div className='form-group' style={{ display: cyfAdmin && showWLAdminField ? 'block' : 'none', marginBottom: 30 }}>
            <Select
              id='white-label-admin-dropdown'
              options={whiteLabelAdminOrgs}
              value={whiteLabelAdminOrgs.find((org) => org.value === user['whiteLabelAdmin']) || null}
              styles={whiteLabelSelect}
              isClearable={true}
              placeholder='White Label Admin'
              onChange={(selected) => {
                const editedUser = { ...user };
                if (!selected) {
                  editedUser['whiteLabelAdmin'] = null;
                  editedUser['portalPermissions'] = null;
                } else {
                  editedUser['whiteLabelAdmin'] = selected.value;
                  if (selected.value === '621784') {
                    editedUser['portalPermissions'] = { portal: ['VIEW_ADMIN', 'CYF_ADMIN'] };
                  } else {
                    editedUser['portalPermissions'] = { portal: ['VIEW_ADMIN', 'WHITELABEL'] };
                  }
                }
                setUser({ ...editedUser, consultantAdmin: null });
              }}
            />
            <Select
              id='consultant-dropdown'
              options={consultantAdminOrgs}
              value={consultantAdminOrgs.find((org) => org.value === user['consultantAdmin']) || null}
              isClearable={true}
              styles={{ ...whiteLabelSelect, control: (provided) => ({ ...provided, marginTop: 15 }) }}
              placeholder='Consultant Admin'
              onChange={(selected) => {
                const editedUser = { ...user };
                if (!selected) {
                  editedUser['consultantAdmin'] = null;
                  editedUser['portalPermissions'] = null;
                } else {
                  editedUser['whiteLabelAdmin'] = null;
                  editedUser['consultantAdmin'] = selected.value;
                  editedUser['portalPermissions'] = { portal: ['VIEW_ADMIN', 'CONSULTANT'] };
                }
                setUser(editedUser);
              }}
            />
          </div>

          <div className='form-group' style={{ marginBottom: 20 }}>
            <Alert severity='info' style={{ display: 'flex', alignItems: 'center' }}>
              <b>
                Please kindly note that new user invitations expire 1 week after being sent. Invitee must login and reset
                their password within that window.
              </b>
            </Alert>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div>
              <AddButton id='submit-button' disabled={processing ? true : false} onClick={handleInviteUser}>
                {processing && <i className='fa fa-spin fa-spinner icon-spinner' />}
                Submit
              </AddButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const allOrgs = state.admin?.organisations;
  const cyfAdmin = state.profile?.details?.cyfAdmin;

  let whiteLabelAdminOrgs, consultantAdminOrgs;

  const permissions = [
    {
      label: 'Organisation Manager',
      value: {
        organisation: { view: true, edit: true, manage: true },
        organisationMetrics: { view: true, create: true, edit: true, delete: true },
        facilities: { '*': { view: true, edit: true, delete: true } },
        facilityMetrics: { view: true, create: true, edit: true, delete: true },
        reports: { view: true, create: true },
        usageData: { view: true, create: true, edit: true, delete: true },
        dashboard: { view: true, create: true, edit: true, delete: true },
        developer: { read: true, write: true },
      },
    },
    {
      label: 'General User',
      value: {
        organisation: { view: true, edit: false, manage: false },
        organisationMetrics: { view: true, create: false, edit: false, delete: false },
        facilities: { '*': { view: true, edit: true, delete: true } },
        facilityMetrics: { view: true, create: true, edit: true, delete: true },
        reports: { view: true, create: true },
        usageData: { view: true, create: true, edit: true, delete: true },
        dashboard: { view: true, create: true, edit: true, delete: true },
        developer: { read: false, write: false },
      },
    },
    {
      label: 'Facility Access',
      value: {
        organisation: { view: false, edit: false, manage: false },
        organisationMetrics: { view: false, create: false, edit: false, delete: false },
        facilities: { '*': { view: true, edit: true, delete: true } },
        facilityMetrics: { view: true, create: true, edit: true, delete: true },
        reports: { view: false, create: false },
        usageData: { view: false, create: false, edit: false, delete: false },
        dashboard: { view: false, create: false, edit: false, delete: false },
        developer: { read: false, write: false },
      },
    },
    {
      label: 'Reports Access',
      value: {
        organisation: { view: false, edit: false, manage: false },
        organisationMetrics: { view: false, create: false, edit: false, delete: false },
        facilities: { '*': { view: false, edit: false, delete: false } },
        facilityMetrics: { view: false, create: false, edit: false, delete: false },
        reports: { view: true, create: true },
        usageData: { view: false, create: false, edit: false, delete: false },
        dashboard: { view: false, create: false, edit: false, delete: false },
        developer: { read: false, write: false },
      },
    },
    {
      label: 'Usage Data Access',
      value: {
        organisation: { view: false, edit: false, manage: false },
        organisationMetrics: { view: false, create: false, edit: false, delete: false },
        facilities: { '*': { view: false, edit: false, delete: false } },
        facilityMetrics: { view: false, create: false, edit: false, delete: false },
        reports: { view: false, create: false },
        usageData: { view: true, create: true, edit: true, delete: true },
        dashboard: { view: false, create: false, edit: false, delete: false },
        developer: { read: false, write: false },
      },
    },
    {
      label: 'View All',
      value: {
        organisation: { view: true, edit: false, manage: false },
        organisationMetrics: { view: true, create: false, edit: false, delete: false },
        facilities: { '*': { view: true, edit: false, delete: false } },
        facilityMetrics: { view: true, create: false, edit: false, delete: false },
        reports: { view: true, create: false },
        usageData: { view: true, create: false, edit: false, delete: false },
        dashboard: { view: true, create: false, edit: false, delete: false },
        developer: { read: false, write: false },
      },
    },
    {
      label: 'Developer',
      value: {
        organisation: { view: false, edit: false, manage: false },
        organisationMetrics: { view: false, create: false, edit: false, delete: false },
        facilities: { '*': { view: false, edit: false, delete: false } },
        facilityMetrics: { view: false, create: false, edit: false, delete: false },
        reports: { view: false, create: false },
        usageData: { view: false, create: false, edit: false, delete: false },
        dashboard: { view: false, create: false, edit: false, delete: false },
        developer: { read: true, write: true },
      },
    },
  ];

  if (cyfAdmin) {
    whiteLabelAdminOrgs = allOrgs && allOrgs.filter((org) => org.id === org.whiteLabelId).map(itemToOptions);
    whiteLabelAdminOrgs && whiteLabelAdminOrgs.push({ label: 'Compare Your Footprint', value: '621784' });
    consultantAdminOrgs = allOrgs && allOrgs.filter((org) => org.id === org.consultantId).map(itemToOptions);
  }

  let orgManager = state?.organisation?.permissions?.organisation?.manage || false;

  return {
    branding: state.branding?.colors,
    cyfAdmin,
    whiteLabelAdminOrgs: _.sortBy(whiteLabelAdminOrgs, ['label']),
    consultantAdminOrgs: _.sortBy(consultantAdminOrgs, ['label']),
    permissions,
    orgManager,
  };
};

const mapDispatchToProps = (dispatch) => ({
  inviteNewUser: (user) => dispatch(AUTHENTICATION_ACTIONS.inviteNewUser(user)),
});

export default connect(mapStateToProps, mapDispatchToProps)(InviteUserForm);
